import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  financeApi,
  type Account,
  type EntryType,
  type JournalVoucher,
} from '@/api/finance';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface NewVoucherShape {
  voucherDate: Dayjs;
  description: string;
  lines: { accountId: number; entryType: EntryType; amount: number; description?: string }[];
}

export function FinanceJournalsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FINANCE:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FINANCE:EDIT'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<JournalVoucher | null>(null);
  const [reversing, setReversing] = useState<JournalVoucher | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [form] = Form.useForm<NewVoucherShape>();

  const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
  const { data, isFetching } = useQuery({
    queryKey: ['finance', 'journals', params],
    queryFn: () => financeApi.listJournals(params.from, params.to),
  });

  const { data: accounts } = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: financeApi.listAccounts,
    staleTime: 60_000,
  });

  const create = useMutation({
    mutationFn: (values: NewVoucherShape) =>
      financeApi.createJournal({
        voucherDate: values.voucherDate.format('YYYY-MM-DD'),
        description: values.description.trim(),
        lines: values.lines.map((l) => ({
          accountId: l.accountId,
          entryType: l.entryType,
          amount: Number(l.amount).toFixed(2),
          description: l.description?.trim() || undefined,
        })),
      }),
    onSuccess: () => {
      message.success('Journal voucher posted');
      qc.invalidateQueries({ queryKey: ['finance'] });
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const reverse = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      financeApi.reverseJournal(id, reason),
    onSuccess: () => {
      message.success('Voucher reversed');
      qc.invalidateQueries({ queryKey: ['finance'] });
      setReversing(null);
      setReverseReason('');
    },
    onError: (e) => message.error(extractError(e)),
  });

  const accountOptions = (accounts ?? [])
    .filter((a: Account) => a.postingAccount && a.active)
    .map((a) => ({ value: a.id, label: `${a.code} · ${a.name}`, type: a.accountType }));

  const watchedLines = Form.useWatch('lines', form) ?? [];
  const dr = watchedLines
    .filter((l: NewVoucherShape['lines'][number]) => l?.entryType === 'DEBIT')
    .reduce((acc: number, l: NewVoucherShape['lines'][number]) => acc + (Number(l?.amount) || 0), 0);
  const cr = watchedLines
    .filter((l: NewVoucherShape['lines'][number]) => l?.entryType === 'CREDIT')
    .reduce((acc: number, l: NewVoucherShape['lines'][number]) => acc + (Number(l?.amount) || 0), 0);
  const balanced = dr > 0 && Math.abs(dr - cr) < 0.005;

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      voucherDate: dayjs(),
      lines: [
        { accountId: undefined as never, entryType: 'DEBIT', amount: 0 },
        { accountId: undefined as never, entryType: 'CREDIT', amount: 0 },
      ],
    });
    setCreating(true);
  };

  const columns: TableProps<JournalVoucher>['columns'] = [
    {
      title: 'Voucher #',
      dataIndex: 'voucherNumber',
      width: 120,
      render: (n: number, row) => (
        <Typography.Text code delete={row.reversed}>JV-{n}</Typography.Text>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'voucherDate',
      width: 130,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (d: string, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text delete={row.reversed}>{d}</Typography.Text>
          {row.reversed && (
            <Tag color="default">Reversed{row.reversalReason ? ` · ${row.reversalReason}` : ''}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'totalDebit',
      width: 150,
      align: 'right',
      render: (a: string, row) => (
        <Typography.Text strong delete={row.reversed}>PKR {PKR(a)}</Typography.Text>
      ),
    },
    {
      title: 'Actions',
      width: 200,
      align: 'right',
      render: (_, row) => (
        <Space>
          <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => setViewing(row)}>
            View
          </Button>
          {canEdit && !row.reversed && (
            <Button
              size="small"
              type="text"
              icon={<RollbackOutlined />}
              onClick={() => setReversing(row)}
            >
              Reverse
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Finance"
        title="Journal vouchers"
        description="Manual balanced entries — adjustments, opening balances, corrections. Each voucher posts to the ledger."
        count={data ? { value: data.length, label: 'vouchers' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New voucher
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No vouchers in this range" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data ?? []}
            pagination={{ pageSize: 25, showSizeChanger: true }}
          />
        </Card>
      )}

      {/* Create modal */}
      <Modal
        title="New journal voucher"
        open={creating}
        onCancel={() => setCreating(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        okText="Post voucher"
        okButtonProps={{ disabled: !balanced }}
        destroyOnHidden
        width={820}
      >
        {creating && (
          <Form form={form} layout="vertical" onFinish={(values) => create.mutate(values)}>
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item label="Date" name="voucherDate" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} md={16}>
                <Form.Item label="Description" name="description" rules={[{ required: true, max: 1000 }]}>
                  <Input placeholder="What is this voucher for?" />
                </Form.Item>
              </Col>
            </Row>

            <Form.List name="lines">
              {(fields, { add, remove }) => (
                <Card
                  size="small"
                  title="Lines"
                  extra={
                    <Button
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => add({ entryType: 'DEBIT', amount: 0 })}
                    >
                      Add line
                    </Button>
                  }
                  style={{ marginBottom: 12 }}
                >
                  {fields.map(({ key, name, ...rest }) => (
                    <Row gutter={8} key={key} align="top" style={{ marginBottom: 8 }}>
                      <Col flex="auto">
                        <Form.Item
                          {...rest}
                          name={[name, 'accountId']}
                          rules={[{ required: true, message: 'Pick an account' }]}
                          noStyle
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="Account"
                            options={accountOptions}
                          />
                        </Form.Item>
                      </Col>
                      <Col flex="120px">
                        <Form.Item {...rest} name={[name, 'entryType']} rules={[{ required: true }]} noStyle>
                          <Select
                            options={[
                              { value: 'DEBIT', label: 'DR' },
                              { value: 'CREDIT', label: 'CR' },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col flex="160px">
                        <Form.Item
                          {...rest}
                          name={[name, 'amount']}
                          rules={[{ required: true, type: 'number', min: 0.01 }]}
                          noStyle
                        >
                          <InputNumber min={0} step={100} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col flex="220px">
                        <Form.Item {...rest} name={[name, 'description']} noStyle>
                          <Input placeholder="Optional note" />
                        </Form.Item>
                      </Col>
                      <Col flex="40px">
                        <Tooltip title="Remove line">
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={fields.length <= 2}
                            onClick={() => remove(name)}
                          />
                        </Tooltip>
                      </Col>
                    </Row>
                  ))}
                </Card>
              )}
            </Form.List>

            <Alert
              type={balanced ? 'success' : 'warning'}
              showIcon
              message={
                <Space>
                  <span>DR PKR {PKR(dr)}</span>
                  <span>·</span>
                  <span>CR PKR {PKR(cr)}</span>
                  <span>·</span>
                  <span>{balanced ? 'Balanced' : `Diff PKR ${PKR(Math.abs(dr - cr).toFixed(2))}`}</span>
                </Space>
              }
            />
          </Form>
        )}
      </Modal>

      {/* View drawer */}
      <Drawer
        title={viewing ? `Voucher JV-${viewing.voucherNumber}` : ''}
        open={viewing != null}
        onClose={() => setViewing(null)}
        size={640}
        destroyOnHidden
      >
        {viewing && <VoucherView voucher={viewing} />}
      </Drawer>

      {/* Reverse modal */}
      <Modal
        title={reversing ? `Reverse JV-${reversing.voucherNumber}` : 'Reverse voucher'}
        open={reversing != null}
        onCancel={() => {
          setReversing(null);
          setReverseReason('');
        }}
        okText="Reverse"
        okButtonProps={{ danger: true, disabled: !reverseReason.trim() || reverse.isPending }}
        confirmLoading={reverse.isPending}
        onOk={() => reversing && reverse.mutate({ id: reversing.id, reason: reverseReason.trim() })}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          A compensating set of ledger entries will be posted to neutralize this voucher.
        </Typography.Paragraph>
        <label style={{ fontWeight: 500 }}>Reason (required)</label>
        <Input.TextArea
          autoFocus
          autoSize={{ minRows: 2, maxRows: 4 }}
          value={reverseReason}
          onChange={(e) => setReverseReason(e.target.value)}
          style={{ marginTop: 6 }}
        />
      </Modal>
    </div>
  );
}

function VoucherView({ voucher }: { voucher: JournalVoucher }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Card size="small">
        <Space orientation="vertical" size={6} style={{ width: '100%' }}>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Date</Typography.Text>
            <div>{dayjs(voucher.voucherDate).format('D MMM YYYY')}</div>
          </div>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Description</Typography.Text>
            <div>{voucher.description}</div>
          </div>
          {voucher.reversed && (
            <Tag color="default">Reversed{voucher.reversalReason ? ` · ${voucher.reversalReason}` : ''}</Tag>
          )}
        </Space>
      </Card>

      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={voucher.lines}
        columns={[
          {
            title: 'Account',
            render: (_, l) => (
              <Space orientation="vertical" size={0}>
                <Typography.Text strong>{l.accountName ?? '—'}</Typography.Text>
                {l.accountCode && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>{l.accountCode}</Typography.Text>
                )}
                {l.description && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>{l.description}</Typography.Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Type',
            width: 80,
            align: 'center',
            render: (_, l) => (
              <Tag color={l.entryType === 'DEBIT' ? 'blue' : 'orange'}>
                {l.entryType === 'DEBIT' ? 'DR' : 'CR'}
              </Tag>
            ),
          },
          {
            title: 'Amount',
            width: 140,
            align: 'right',
            render: (_, l) => `PKR ${PKR(l.amount)}`,
          },
        ]}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={2}>
              <Typography.Text strong>Totals</Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              <Space orientation="vertical" size={0} align="end">
                <Typography.Text>DR PKR {PKR(voucher.totalDebit)}</Typography.Text>
                <Typography.Text>CR PKR {PKR(voucher.totalCredit)}</Typography.Text>
              </Space>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
}
